import { CheckCircle, List, Tag } from 'lucide-react'
import { SiGoogleads } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsStructuredSnippetManagement,
  type GoogleAdsStructuredSnippetManagementResult,
} from 'sprites-types/workflow/tools/googleAdsStructuredSnippetManagement'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'

function GoogleAdsStructuredSnippetManagementDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsStructuredSnippetManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsStructuredSnippetManagementResult
  const { linkTarget, structuredSnippetId } = result

  // Get input data from nodeExecution
  const inputs = nodeExecution.inputs as any
  const header = inputs?.functionArgs?.action?.params?.header || ''
  const values = inputs?.functionArgs?.action?.params?.values || []
  const targetId =
    inputs?.functionArgs?.action?.params?.campaignId ||
    inputs?.functionArgs?.action?.params?.assetGroupId ||
    inputs?.functionArgs?.action?.params?.adGroupId ||
    ''

  const targetType =
    linkTarget === 'assetGroup' ? 'Asset Group' : linkTarget === 'adGroup' ? 'Ad Group' : 'Campaign'

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-3.5">
        <div className="mt-0.5 flex-shrink-0">
          <div className="rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 p-2.5 shadow-lg">
            <List className="h-5 w-5 text-white" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-center gap-2">
            <SiGoogleads className="h-4 w-4 text-[#4285f4]" />
            <div className="text-sm font-semibold text-gray-900">
              Structured Snippet Extension Added
            </div>
            <div className="ml-auto">
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Active
              </span>
            </div>
          </div>

          {/* Preview Card */}
          <div className="mb-3 rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-3 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <Tag className="h-4 w-4 text-indigo-600" />
              <span className="text-sm font-semibold text-indigo-900">{header}</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {values.map((value: string, index: number) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-lg bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-900"
                >
                  {value}
                </span>
              ))}
            </div>

            <div className="mt-3 text-xs text-gray-600">
              {values.length} {values.length === 1 ? 'item' : 'items'} will appear under this header
              to showcase your offerings
            </div>
          </div>

          {/* Metadata */}
          <div className="rounded-lg bg-gray-50 px-3 py-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-500">Target:</span>
                <span className="ml-1.5 text-gray-900">{targetType}</span>
              </div>
              <div>
                <span className="font-medium text-gray-500">ID:</span>
                <span className="ml-1.5 font-mono text-gray-900">{targetId}</span>
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-500">Snippet ID:</span>
                <span className="ml-1.5 font-mono text-gray-900">{structuredSnippetId}</span>
              </div>
            </div>
          </div>

          <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600">
            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
            <span>Extension is live and will appear with your ads</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function downloadGoogleAdsStructuredSnippetManagement(
  _nodeExecution: NodeExecution
): Promise<void> {
  return Promise.resolve()
}

function googleAdsStructuredSnippetManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsStructuredSnippetManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\\n\\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as GoogleAdsStructuredSnippetManagementResult
  const { linkTarget, structuredSnippetId } = result

  // Get input data from nodeExecution
  const inputs = nodeExecution.inputs as any
  const header = inputs?.functionArgs?.action?.params?.header || ''
  const values = inputs?.functionArgs?.action?.params?.values || []
  const targetId =
    inputs?.functionArgs?.action?.params?.campaignId ||
    inputs?.functionArgs?.action?.params?.assetGroupId ||
    inputs?.functionArgs?.action?.params?.adGroupId ||
    ''

  const targetType =
    linkTarget === 'assetGroup' ? 'Asset Group' : linkTarget === 'adGroup' ? 'Ad Group' : 'Campaign'

  return `## Google Ads Structured Snippet Added

**Header:** ${header}
**Values:** ${values.join(', ')}
**${targetType} ID:** ${targetId}
**Snippet ID:** ${structuredSnippetId}

---
`
}

export const GoogleAdsStructuredSnippetManagementDisplay = createWorkflowToolComponent(
  GoogleAdsStructuredSnippetManagementDisplayComponent,
  downloadGoogleAdsStructuredSnippetManagement,
  googleAdsStructuredSnippetManagementToMarkdown
)
